import {
    arrow,
    autoUpdate,
    flip,
    FloatingArrow,
    FloatingPortal,
    offset,
    safePolygon,
    shift,
    useDismiss,
    useFloating,
    useFocus,
    useHover,
    useInteractions,
    useRole,
    useTransitionStyles,
} from '@floating-ui/react';
import classNames from 'classnames';
import { useRef, useState, type ReactNode } from 'react';
import vars from '../../../vars';
import styles from './style.m.less';

export type TooltipPosition = 'top' | 'left' | 'right' | 'bottom';

interface Props {
    children: ReactNode;
    tooltip: ReactNode | null | undefined;
    /** Intended placement of the tooltip. Might be overridden based on available space. */
    position?: TooltipPosition;
    /** Render the container inline (instead of as a block). */
    inline?: boolean;
    oneline?: boolean;
    /** If true, skips the initial delay of the tooltip. */
    instant?: boolean;
    /** Always show the tooltip (without hover). */
    visible?: boolean;
    /** Makes the content of the tooltip clickable (e.g. to follow links). */
    clickable?: boolean;
    triggerClassName?: string;
}

export default function Tooltip({
    children,
    tooltip,
    position = 'top',
    inline = false,
    oneline,
    instant,
    visible: alwaysOpen = false,
    clickable,
    triggerClassName,
}: Props) {
    'use memo';
    const hasTooltip = Boolean(tooltip);
    const [isOpen, setIsOpen] = useState(alwaysOpen);
    const arrowRef = useRef(null);
    const { refs, floatingStyles, context } = useFloating({
        placement: position,
        open: hasTooltip && isOpen,
        onOpenChange: hasTooltip && !alwaysOpen ? setIsOpen : undefined,
        middleware: [
            // eslint-disable-next-line react-hooks/refs
            arrow({ element: arrowRef }),
            offset(10),
            flip(),
            shift(),
        ],
        whileElementsMounted: autoUpdate,
    });

    const hover = useHover(context, {
        move: false,
        handleClose: clickable ? safePolygon() : null,
        delay: {
            open: instant ? 0 : 500,
            close: 0,
        },
    });
    const focus = useFocus(context);
    const dismiss = useDismiss(context);
    const role = useRole(context, { role: clickable ? 'dialog' : 'tooltip' });

    const { isMounted, styles: transitionStyles } = useTransitionStyles(context, { duration: 200 });
    const { getReferenceProps, getFloatingProps } = useInteractions([hover, focus, dismiss, role]);

    const Container = inline ? 'span' : 'div';
    return (
        <>
            <Container
                ref={refs.setReference}
                {...getReferenceProps({
                    className: classNames(triggerClassName, {
                        [styles.triggerInline]: inline,
                    }),
                })}
            >
                {children}
            </Container>
            {hasTooltip && isMounted && (
                <FloatingPortal>
                    <div
                        // eslint-disable-next-line react-hooks/refs
                        ref={refs.setFloating}
                        style={{
                            ...floatingStyles,
                            ...(alwaysOpen ? {} : transitionStyles),
                        }}
                        className={classNames(styles.tooltip, { [styles.tooltipOneline]: oneline })}
                        {...getFloatingProps()}
                    >
                        {tooltip}
                        <FloatingArrow ref={arrowRef} context={context} fill={vars.tooltipBackground} />
                    </div>
                </FloatingPortal>
            )}
        </>
    );
}
